/**
 * Login. MASTER_PROMPT.md section 7.8.
 *
 * The left panel shows a live plant heartbeat from an unauthenticated endpoint
 * that exposes counts only. It is the detail that makes the screen feel
 * connected to a real factory rather than merely designed.
 */

import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { auth } from "../../lib/auth";
import type { Heartbeat, LoginResponse } from "../../lib/types";
import { relativeTime } from "../../lib/format";

const PARTICLE_COUNT = 12;

type Stage = "credentials" | "twoFactor";

interface Props {
  onSignedIn: (session: LoginResponse) => void;
}

/** Deterministic scatter — a fresh random layout on every keystroke would
 *  make the panel twitch as React re-renders. */
function useParticles() {
  return useMemo(
    () =>
      Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
        left: `${(i * 37 + 11) % 96}%`,
        delay: `${(i * 2.7) % 18}s`,
        duration: `${22 + ((i * 5) % 16)}s`,
        top: `${70 + ((i * 13) % 30)}%`,
      })),
    []
  );
}

export function LoginScreen({ onSignedIn }: Props) {
  const [stage, setStage] = useState<Stage>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const [beat, setBeat] = useState<Heartbeat | null>(null);
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  // Recovery codes are `A3F91-2B7C0` — eleven characters, hex letters
  // included. The six digit boxes strip non-digits and cap at six, so a code
  // could never be typed into them however clearly the hint said to. It gets
  // its own field.
  const [useRecovery, setUseRecovery] = useState(false);
  const [recovery, setRecovery] = useState("");
  const pending = useRef<LoginResponse | null>(null);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const particles = useParticles();

  // Poll the heartbeat gently. A failure is silent — the login must work with
  // the endpoint down.
  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .heartbeat()
        .then((h) => alive && setBeat(h))
        .catch(() => undefined);
    load();
    const timer = setInterval(load, 15_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  // Rate-limit countdown, shown rather than left to guesswork.
  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    const t = setTimeout(() => setLockoutSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [lockoutSeconds]);

  useEffect(() => {
    const saved = localStorage.getItem("npi.email");
    if (saved) setEmail(saved);
  }, []);

  async function submitCredentials(event: FormEvent) {
    event.preventDefault();
    if (busy || lockoutSeconds > 0) return;
    setBusy(true);
    setError(null);
    try {
      const session = await auth.login(email, password);
      if (remember) localStorage.setItem("npi.email", email);
      else localStorage.removeItem("npi.email");

      // `mfa_required` means the server issued a CHALLENGE, not a session.
      // That token opens nothing until a code is verified, so this branch is
      // not a courtesy — it is the only way forward.
      if (session.mfa_required) {
        pending.current = session;
        setStage("twoFactor");
        setBusy(false);
        window.setTimeout(() => otpRefs.current[0]?.focus(), 60);
        return;
      }
      setDone(true);
      window.setTimeout(() => onSignedIn(session), 420);
    } catch (err) {
      const e = err as ApiError;
      if (e.status === 429) {
        const match = /(\d+) seconds/.exec(e.message);
        setLockoutSeconds(match ? Number(match[1]) : 60);
      }
      setError(e.message);
      setBusy(false);
    }
  }

  function setOtpDigit(index: number, value: string) {
    const digits = value.replace(/\D/g, "");
    if (!digits) {
      setOtp((prev) => prev.map((d, i) => (i === index ? "" : d)));
      return;
    }
    // Paste support: a six-digit paste fills every box from here.
    setOtp((prev) => {
      const next = [...prev];
      for (let i = 0; i < digits.length && index + i < 6; i += 1) {
        next[index + i] = digits[i];
      }
      return next;
    });
    const landed = Math.min(index + digits.length, 5);
    otpRefs.current[landed]?.focus();
  }

  async function submitOtp(event: FormEvent) {
    event.preventDefault();
    const code = useRecovery ? recovery.trim() : otp.join("");
    if (code.length < 6 || !pending.current) return;

    setBusy(true);
    setError(null);
    try {
      // The server exchanges the challenge for a session. A wrong code means
      // no session at all — there is nothing for the client to wave through.
      const session = await auth.verifyMfa(code);
      setDone(true);
      window.setTimeout(() => onSignedIn(session), 420);
    } catch (err) {
      setError((err as ApiError).message);
      if (useRecovery) {
        setRecovery("");
      } else {
        setOtp(Array(6).fill(""));
        window.setTimeout(() => otpRefs.current[0]?.focus(), 60);
      }
      setBusy(false);
    }
  }

  const disabled = busy || done || lockoutSeconds > 0;

  return (
    <div className="login">
      <section className="login__visual" aria-hidden="true">
        <div className="login__mesh" />
        <div className="login__grain" />
        <div className="login__particles">
          {particles.map((p, i) => (
            <span
              key={i}
              className="login__particle"
              style={{
                left: p.left,
                top: p.top,
                animationDelay: p.delay,
                animationDuration: p.duration,
              }}
            />
          ))}
        </div>

        <div className="login__brandmark">
          <span className="login__diamond" />
          NEG
        </div>

        <h1 className="login__tagline">
          Every gram
          <br />
          accounted for.
          <br />
          <span>Scrap to dispatch.</span>
        </h1>

        <div className="heartbeat">
          <div className="heartbeat__row">
            <span
              className={`heartbeat__dot${
                beat && beat.looms_running > 0 ? "" : " heartbeat__dot--idle"
              }`}
            />
            <span>
              <span className="heartbeat__value">
                {beat ? beat.looms_running : "—"}
              </span>{" "}
              of{" "}
              <span className="heartbeat__value">
                {beat ? beat.looms_total : "—"}
              </span>{" "}
              looms running
            </span>
          </div>
          <div className="heartbeat__row">
            <span className="heartbeat__dot heartbeat__dot--idle" />
            <span>
              Last sync{" "}
              <span className="heartbeat__value">
                {beat?.last_movement_at
                  ? relativeTime(beat.last_movement_at)
                  : "—"}
              </span>
            </span>
          </div>
          <div className="heartbeat__row">
            <span className="heartbeat__dot heartbeat__dot--idle" />
            <span>
              {beat?.active_stage ?? "Plant idle"} · {beat?.shift ?? "—"}
            </span>
          </div>
        </div>

        {/* Which build the plant is on, before anybody signs in. The first
            question asked when a screen looks wrong, and needing a password
            to answer it is backwards. */}
        <p className="login__version">
          NEG Plant Intelligence
          <span className="login__version-num">
            v{beat?.version ?? "—"}
          </span>
        </p>
      </section>

      <section className="login__panel">
        <div className="glass login__card">
          {stage === "credentials" ? (
            <form onSubmit={submitCredentials} noValidate>
              <p className="login__greeting">
                <strong>{beat?.greeting ?? "Welcome"}.</strong>
                {beat?.shift ?? "Sign in to continue"}
              </p>

              {error && (
                <div className="login__banner" role="alert">
                  <span aria-hidden="true">!</span>
                  <span>
                    {lockoutSeconds > 0
                      ? `Too many failed attempts. Try again in ${lockoutSeconds}s.`
                      : error}
                  </span>
                </div>
              )}

              <div className="field">
                <input
                  id="email"
                  className="field__input"
                  type="email"
                  placeholder=" "
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus={!email}
                />
                <label className="field__label" htmlFor="email">
                  Email
                </label>
              </div>

              <div className="field">
                <input
                  id="password"
                  className="field__input"
                  type={reveal ? "text" : "password"}
                  placeholder=" "
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus={Boolean(email)}
                />
                <label className="field__label" htmlFor="password">
                  Password
                </label>
                <button
                  type="button"
                  className="field__reveal"
                  onClick={() => setReveal((r) => !r)}
                  aria-label={reveal ? "Hide password" : "Show password"}
                >
                  {reveal ? "Hide" : "Show"}
                </button>
              </div>

              <div className="login__options">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  Remember me
                </label>
              </div>

              <button className="btn" type="submit" disabled={disabled}>
                {done ? (
                  <>✓ Signed in</>
                ) : busy ? (
                  <>
                    <span className="spinner" /> Signing in
                  </>
                ) : (
                  "Sign in"
                )}
              </button>

              <div className="login__divider">or</div>

              <button
                className="btn btn--ghost"
                type="button"
                onClick={() =>
                  setError(
                    "Passkeys are not registered on this device yet. Sign in with your password once to enrol."
                  )
                }
              >
                Use a passkey
              </button>
            </form>
          ) : (
            <form onSubmit={submitOtp} noValidate>
              <p className="login__greeting">
                <strong>Verify it&rsquo;s you.</strong>
                {useRecovery
                  ? "Enter one of your recovery codes"
                  : "Enter the 6-digit code from your authenticator"}
              </p>

              {error && (
                <div className="login__banner" role="alert">
                  <span aria-hidden="true">!</span>
                  <span>{error}</span>
                </div>
              )}

              {useRecovery ? (
                <div className="field">
                  <input
                    id="recovery"
                    className="field__input mono"
                    placeholder=" "
                    autoComplete="one-time-code"
                    autoCapitalize="characters"
                    spellCheck={false}
                    maxLength={14}
                    value={recovery}
                    onChange={(e) => setRecovery(e.target.value.toUpperCase())}
                    aria-label="Recovery code"
                    autoFocus
                  />
                  <label className="field__label" htmlFor="recovery">
                    Recovery code
                  </label>
                </div>
              ) : (
                <div className="otp">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => {
                        otpRefs.current[i] = el;
                      }}
                      className="otp__box"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={digit}
                      aria-label={`Digit ${i + 1}`}
                      onChange={(e) => setOtpDigit(i, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !otp[i] && i > 0) {
                          otpRefs.current[i - 1]?.focus();
                        }
                      }}
                    />
                  ))}
                </div>
              )}

              <button
                className="btn"
                type="submit"
                disabled={
                  (useRecovery
                    ? recovery.trim().length < 6
                    : otp.join("").length < 6) ||
                  done ||
                  busy
                }
              >
                {done ? (
                  <>✓ Verified</>
                ) : busy ? (
                  <>
                    <span className="spinner" /> Verifying
                  </>
                ) : (
                  "Verify"
                )}
              </button>

              <div className="login__divider">or</div>

              <button
                className="btn btn--ghost"
                type="button"
                onClick={() => {
                  setUseRecovery((r) => !r);
                  setOtp(Array(6).fill(""));
                  setRecovery("");
                  setError(null);
                }}
              >
                {useRecovery
                  ? "Use my authenticator"
                  : "Use a recovery code"}
              </button>

              <button
                className="btn btn--ghost"
                type="button"
                onClick={() => {
                  setStage("credentials");
                  setUseRecovery(false);
                  setOtp(Array(6).fill(""));
                  setRecovery("");
                  setError(null);
                }}
              >
                Back
              </button>

              <p className="login__hint">
                {useRecovery
                  ? "One of the codes issued when you enrolled, like A3F91-2B7C0. Each works once."
                  : "Lost your authenticator? Use a recovery code instead."}
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
